import { pick } from 'lodash-es';
import Analytics from '../../../utils/analytics';

import {
  getItemFromTimeline,
  determineSubIndex,
} from '../../../utils/production';

function sendPreviewSettings(state, rootState) {
  const { production } = rootState.production;
  const preview = state.production;

  vm.$socket.emit(
    'previewSettings',
    {
      preview: true,
      production_id: production._id,
      videoPlaying: preview.videoPlaying,

      // Shared settings pulled from base production
      ...pick(production, [
        'playing',
        'muted',
        'looped',
        'backgroundColor',
        'transition',
        'transitionMode',
      ]),
    },
  );
}

const state = {
  isReady: false,
  credentials: {
    production_id: null,
    preview_room: null,
    screen_id: null,
    verification_key: null,
  },
  production: {
    index: null,
    sub_index: null,
    videoPlaying: false,
  },
  videoDuration: 0,
};

const actions = {
  socket_preview_created({ commit }, payload) {
    // REMOVE THIS AFTER SHIPPING SOCKETS UPDATES
    if (payload.credentials) {
      commit('UPDATE_PREVIEW', payload);
    } else {
      const oldPayload = {
        production: {},
        credentials: payload,
      };

      commit('UPDATE_PREVIEW', oldPayload);
    }
  },

  setupPreview({ commit, rootState }) {
    const { id } = rootState.production.production;
    // state for curtain and play need to be reset during setup for preview or they bleed into
    // other productions while navigating between productions in a single session
    commit('RESET_TO_DEFAULTS');
    vm.$socket.emit('setupPreview', { production_id: id });
  },

  resetPreview({ commit }) {
    commit('RESET_PREVIEW');
  },

  // Everything below here, should probably be moved to production store
  clearPreview({ commit, rootState }) {
    commit('RESET_TO_DEFAULTS');

    // PULLING THESE OFF STATE IS GROSS
    commit('PREVIEW_UPDATE_TIMELINE', {
      rootState,
      client_id: rootState.production.production._id,
      index: state.production.index,
      sub_index: state.production.sub_index,
    });
  },

  togglePreviewPost(context, payload) {
    const { commit, rootState } = context;

    const { parentTimelineId, item } = payload;

    const post = rootState.posts.lookupTable[item.post_id];

    const postTimelineId = item.timeline_id;
    const isLayoutLive = (state.production.index === parentTimelineId);

    if (isLayoutLive) {
      const isPostAlreadyLive = (state.production.sub_index === postTimelineId);
      const subIndex = isPostAlreadyLive ? null : postTimelineId;

      const event = isPostAlreadyLive ? 'Off' : 'On';

      Analytics.log({
        event: `Producer Toggled Preview Post ${event}`,
        fields: {
          production_id: state.credentials.production_id,
          post_id: item.post_id,
          network: item.network,
          permalink: post?.permalink,
        },
        ownerId: rootState.production.production.owner,
      });

      commit('PREVIEW_SET_SUB_INDEX', {
        index: parentTimelineId,
        sub_index: subIndex,
      });

      commit('PREVIEW_UPDATE_TIMELINE', {
        rootState,
        index: parentTimelineId,
        sub_index: subIndex,
      });
    }
  },

  togglePreview(context, { timelineId }) {
    const preview = context.state.production;
    const { production } = context.rootState.production;

    const item = getItemFromTimeline(timelineId, production.timeline);

    const isAlreadyLive = (state.production.index === timelineId);
    const subIndex = determineSubIndex(item);

    const event = isAlreadyLive ? 'Off' : 'Live';

    context.commit('PREVIEW_SET_SUB_INDEX', {
      index: isAlreadyLive ? null : timelineId,
      sub_index: isAlreadyLive ? null : subIndex,
    });

    if (isAlreadyLive) {
      // If nothing is live (in preview) anymore...
      // .. then pause any posts that were playing
      context.dispatch('stopPreviewVideo');
    } else if (timelineId === production.index && preview.videoPlaying !== production.videoPlaying) {
      // If the new preview layout is already "Live",
      // then copy the videoPlaying state from real production
      context.dispatch('setPreviewVideoPlaying', production.videoPlaying);
    }

    // this fires from the `production // store module` (unfortunately)
    context.commit('PREVIEW_UPDATE_TIMELINE', {
      rootState: context.rootState,
      index: isAlreadyLive ? null : timelineId,
      sub_index: isAlreadyLive ? null : subIndex,
    });

    Analytics.toggleLiveEvent({
      event,
      item,
      rootState: context.rootState,
      preview: true,
    });
  },

  updatePreviewTimeline(context) {
    const { index } = state.production;

    // TODO: Remove the '0' strings everywhere once we move away from it.
    //       We should just use null instead.
    if (!index || index === '0') {
      // Nothing live, so don't bother sending changes over socket
      return;
    }

    // Ensure sub_index is set to the proper element
    context.dispatch('syncPreviewSubIndex');

    context.commit('PREVIEW_UPDATE_TIMELINE', {
      rootState: context.rootState,
      client_id: context.state.production._id,
      index: context.state.production.index,
      sub_index: context.state.production.sub_index,
    });
  },

  /**
   * Force sub_index to sync with current preview element
   */
  syncPreviewSubIndex(context) {
    const { index } = context.state.production;
    const { timeline } = context.rootState.production.production;

    if (!index || index === '0') {
      // Reset both both
      context.commit('PREVIEW_SET_SUB_INDEX', {
        index,
        sub_index: null,
      });

      return;
    }

    const item = getItemFromTimeline(index, timeline);
    context.commit('PREVIEW_SET_SUB_INDEX', {
      index,
      sub_index: determineSubIndex(item),
    });
  },

  playPreviewVideo(context) {
    context.commit('PREVIEW_SET_VIDEO_PLAYING', true);
    sendPreviewSettings(context.state, context.rootState);
  },

  stopPreviewVideo(context) {
    context.commit('PREVIEW_SET_VIDEO_PLAYING', false);
    sendPreviewSettings(context.state, context.rootState);
  },

  setPreviewVideoPlaying(context, videoPlaying) {
    context.commit('PREVIEW_SET_VIDEO_PLAYING', videoPlaying);
    sendPreviewSettings(context.state, context.rootState);
  },

  togglePreviewVideo(context) {
    if (context.state.production.videoPlaying) {
      context.commit('PREVIEW_SET_VIDEO_PLAYING', false);
    } else {
      context.commit('PREVIEW_SET_VIDEO_PLAYING', true);
    }

    sendPreviewSettings(context.state, context.rootState);
  },

  sendPreviewAction(context, payload) {
    vm.$socket.emit('handlePreviewAction', {
      preview_room: state.credentials.preview_room,
      action: payload,
    });
  },

  sendPreviewSettings({ rootState }) {
    sendPreviewSettings(state, rootState);
  },

  removeTimelineItemPreview({ commit, rootState }, { timelineId }) {
    const isLayoutLive = (state.production.index === timelineId);

    // If this was in preview, then we need to reset it.
    if (isLayoutLive) {
      commit('PREVIEW_SET_SUB_INDEX', {
        index: null,
        sub_index: null,
      });

      commit('PREVIEW_UPDATE_TIMELINE', {
        rootState,
        index: null,
        sub_index: null,
      });
    }
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  PREVIEW_TIMELINE_SETTINGS(state, payload) {
    state.production = {
      ...state.production,
      ...payload,
    };
  },

  CLEAR_TIMELINE(state) {
    state.production.videoPlaying = false;
    state.videoDuration = 0;
  },

  UPDATE_PREVIEW(state, payload) {
    state.credentials = { ...state.credentials, ...payload.credentials };
    state.production = { ...state.production, ...payload.production };
    state.isReady = true;
  },

  RESET_PREVIEW(state) {
    state.isReady = false;
  },

  RESET_TO_DEFAULTS(state) {
    state.production.index = null;
    state.production.sub_index = null;
    state.production.videoPlaying = false;
  },

  PREVIEW_SET_SUB_INDEX(state, payload) {
    const { production } = state;
    const { index, sub_index: subIndex } = payload;

    // Clear stored video duration if values changed
    if (production.index !== index || production.sub_index !== subIndex) {
      state.videoDuration = 0;
    }

    state.production.index = index;
    state.production.sub_index = subIndex;
  },

  PREVIEW_SET_VIDEO_PLAYING(state, videoPlaying) {
    state.production.videoPlaying = videoPlaying;
  },

  SET_PREVIEW_VIDEO_DURATION(state, payload) {
    state.videoDuration = payload;
  },
};

const getters = {
  previewConnectionReady(state) {
    return state.isReady;
  },

  previewUrl(state) {
    const creds = state.credentials;

    return state.isReady ? `${process.env.TAGBOARD_LIVE_URL}/preview/${creds.production_id}?screen_id=${creds.screen_id}&verification_key=${creds.verification_key}` : '';
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
